import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputProps,
} from '@everlywell/ui-kit';
import { get } from 'lodash';
import React, { forwardRef } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { mergeRefs } from 'utils/react';

export type InputFieldProps = InputProps & {
  label: string;
  helperText?: string;
  name: string;
  placeholder?: string;
  validations?: RegisterOptions;
  isFloatingLabel?: boolean;
};

/**
 * Base Input Field.
 * This component needs to be wrapped in a `FormProvider` component, since it uses `useFormContext`.
 */
function InputField(
  props: InputFieldProps,
  ref: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  const {
    label,
    helperText,
    name,
    placeholder,
    type = 'text',
    validations,
    isFloatingLabel = false,
    ...rest
  } = props;
  const { register, errors } = useFormContext();

  const errorMessage = get(errors, name)?.message; // account for nested fields, ie "name.first"
  const hasError = Boolean(errorMessage);
  const isRequired = Boolean(validations?.required);

  return (
    <FormControl
      isInvalid={hasError}
      isRequired={isRequired}
      variant={isFloatingLabel ? 'floating' : 'default'}
    >
      {label && !isFloatingLabel ? <FormLabel>{label}</FormLabel> : null}
      <Input
        ref={mergeRefs(ref, register(validations))}
        name={name}
        type={type}
        placeholder={placeholder ?? ''}
        {...rest}
        // this is to prevent the browser from showing the default required message
        // since we'll have our own message
        required={false}
      />
      {/* floating label functionality requires the label to be placed after the input */}
      {label && isFloatingLabel ? <FormLabel>{label}</FormLabel> : null}
      {!hasError && helperText ? (
        <FormHelperText>{helperText}</FormHelperText>
      ) : null}
      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
}

export default forwardRef(InputField);
